/* eslint-disable react-hooks/static-components */
import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Download, Loader2, ArrowUpDown, Calendar as CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Button } from '@/app/components/ui/button';
import { Calendar } from '@/app/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/app/components/ui/popover';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/app/components/ui/table';
import { useUtilizationReport } from '@/hooks/use-reports';
import { cn } from '@/app/components/ui/utils';
import { formatDateOnly } from '@/lib/date-utils';

type SortField = 'asset_name' | 'serial_number' | 'utilization_percentage' | 'deployed_days' | 'idle_days';
type SortDirection = 'asc' | 'desc';

export function UtilizationReportPage() {
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const isDefaultRange = !startDate && !endDate;
  const [sortField, setSortField] = useState<SortField>('utilization_percentage');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [isExporting, setIsExporting] = useState(false);

  const filters = {
    start_date: startDate ? format(startDate, 'yyyy-MM-dd') : undefined,
    end_date: endDate ? format(endDate, 'yyyy-MM-dd') : undefined,
  };

  const { data, isLoading, error } = useUtilizationReport(filters);

  // eslint-disable-next-line react-hooks/preserve-manual-memoization
  const sortedData = useMemo(() => {
    if (!data?.results) return [];
    
    return [...data.results].sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case 'asset_name':
          comparison = a.asset_name.localeCompare(b.asset_name);
          break;
        case 'serial_number':
          comparison = a.serial_number.localeCompare(b.serial_number);
          break;
        case 'utilization_percentage':
          comparison = a.utilization_percentage - b.utilization_percentage;
          break;
        case 'deployed_days':
          comparison = a.deployed_days - b.deployed_days;
          break;
        case 'idle_days':
          comparison = a.idle_days - b.idle_days;
          break;
      }
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [data?.results, sortField, sortDirection]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const now = new Date();
      const csvContent = [
        ['Utilization Report'],
        [`Date Range: ${filters.start_date || 'All'} to ${filters.end_date || 'All'}`],
        [],
        ['Asset Name', 'Serial Number', 'Utilization %', 'Deployed Days', 'Idle Days'],
        ...sortedData.map(item => [
          item.asset_name,
          item.serial_number,
          item.utilization_percentage.toFixed(1),
          item.deployed_days.toString(),
          item.idle_days.toString()
        ])
      ].map(row => row.join(',')).join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `utilization-report-${now.toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } finally {
      setIsExporting(false);
    }
  };

  const SortableHeader = ({ field, children }: { field: SortField; children: React.ReactNode }) => (
    <TableHead>
      <Button
        variant="ghost"
        onClick={() => handleSort(field)}
        className="h-8 px-2 -ml-2 font-medium hover:bg-transparent"
      >
        {children}
        <ArrowUpDown className={cn(
          "ml-2 h-4 w-4",
          sortField === field ? "opacity-100" : "opacity-50"
        )} />
      </Button>
    </TableHead>
  );

  if (isLoading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <p className="text-red-600">Failed to load utilization report. Please try again later.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Utilization Report</h1>
          <p className="text-gray-600 mt-1">Analyze equipment usage and efficiency</p>
        </div>
        <Button variant="outline" onClick={handleExport} disabled={isExporting || !sortedData.length}>
          {isExporting ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Download className="h-4 w-4 mr-2" />
          )}
          Export CSV
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            Date Range
            {isDefaultRange && (
              <span className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">
                Showing last 30 days
              </span>
            )}
          </CardTitle>
          <CardDescription>
            {isDefaultRange
              ? 'No custom range selected — showing the last 30 days of utilization data.'
              : 'Select the period for utilization analysis'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium">Start Date</label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-[240px] justify-start text-left font-normal",
                      !startDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {startDate ? format(startDate, "PPP") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={startDate}
                    onSelect={setStartDate}
                    autoFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium">End Date</label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-[240px] justify-start text-left font-normal",
                      !endDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {endDate ? format(endDate, "PPP") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={endDate}
                    onSelect={setEndDate}
                    autoFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Asset Utilization</CardTitle>
          <CardDescription>
            {data?.total_assets || 0} assets analyzed
            {data?.start_date && data?.end_date && (
              <span> from {formatDateOnly(data.start_date)} to {formatDateOnly(data.end_date)}</span>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {sortedData.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-8">No utilization data available for the selected period</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableHeader field="asset_name">Asset Name</SortableHeader>
                  <SortableHeader field="serial_number">Serial Number</SortableHeader>
                  <SortableHeader field="utilization_percentage">Utilization %</SortableHeader>
                  <SortableHeader field="deployed_days">Deployed Days</SortableHeader>
                  <SortableHeader field="idle_days">Idle Days</SortableHeader>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedData.map((item) => (
                  <TableRow key={item.asset_id}>
                    <TableCell>
                      <Link
                        to={`/assets/${item.asset_id}`}
                        className="text-blue-600 hover:underline font-medium"
                      >
                        {item.asset_name}
                      </Link>
                    </TableCell>
                    <TableCell className="text-gray-600">{item.serial_number}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="w-16 bg-gray-200 rounded-full h-2">
                          <div
                            className={cn(
                              "h-2 rounded-full",
                              item.utilization_percentage >= 75 ? "bg-green-500" :
                              item.utilization_percentage >= 50 ? "bg-yellow-500" :
                              item.utilization_percentage >= 25 ? "bg-orange-500" : "bg-red-500"
                            )}
                            style={{ width: `${Math.min(item.utilization_percentage, 100)}%` }}
                          />
                        </div>
                        <span className="text-sm font-medium">{item.utilization_percentage.toFixed(1)}%</span>
                      </div>
                    </TableCell>
                    <TableCell>{item.deployed_days}</TableCell>
                    <TableCell>{item.idle_days}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
